'use client';

import { useLocale } from 'next-intl';
import Script from 'next/script';
import { useEffect, useState } from 'react';

import { usePathname } from '@commons/i18n/navigation';
import { COOKIE_PRO_DOMAIN, IS_PRODUCTION } from '@commons/utils/constants';

/**
 * OneTrust SDK injected by CookiePro.
 *
 * We need to add it to the global window object to avoid TypeScript errors.
 *
 * @see https://my.onetrust.com/s/article/UUID-69162cb7-c4a2-ac70-39a1-ca69c9340046?language=en_US
 */

type WindowWithOneTrust = Window & {
  OneTrust: {
    LoadBanner: () => void;
    initializeCookiePolicyHtml: () => void;
    changeLanguage: (lang: string) => void;
  };
};

declare const window: WindowWithOneTrust;

/**
 * CookiePro SDK script.
 *
 * IMPORTANT:
 * - This script should only be placed in the root layout.
 *
 * - When a route change, React re-renders some components HTML.
 *   This also removes any events or elements that were added by the OneTrust SDK.
 *   To fix this, we trigger an effect an route change that re-initializes the OneTrust SDK.
 */

export const CookieProSDKScript = () => {
  const locale = useLocale();
  const pathname = usePathname();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (isReady && window.OneTrust) {
      window.OneTrust.LoadBanner();
      window.OneTrust.initializeCookiePolicyHtml();

      // Store selected language
      localStorage.setItem('OneTrustLang', locale);

      // Update OneTrust language
      window.OneTrust.changeLanguage(locale);
    }
  }, [pathname, locale, isReady]);

  if (!IS_PRODUCTION) return null;

  return (
    <Script
      strategy="afterInteractive"
      src="https://cookie-cdn.cookiepro.com/scripttemplates/otSDKStub.js"
      type="text/javascript"
      charSet="UTF-8"
      data-domain-script={COOKIE_PRO_DOMAIN}
      data-document-language="true"
      onReady={() => {
        setIsReady(true);
      }}
    />
  );
};

/**
 * CookiePro Optanon script.
 *
 * IMPORTANT: This script should only be placed in the root layout.
 */

export const CookieProOptanonScript = () => {
  if (!IS_PRODUCTION) return null;

  return (
    <Script
      strategy="afterInteractive"
      id="cookiepro-optanonwrapper"
      dangerouslySetInnerHTML={{
        __html: `
          function OptanonWrapper() {}
        `,
      }}
    />
  );
};
